import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { sv } from "date-fns/locale";
import { GraduationCap, Clock, Calendar, Loader2, CheckCircle2 } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import { useTranslation } from "react-i18next";

const dayNames = ["Söndag", "Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag"];

export default function CoachBooking() {
  // PR-AV: useTranslation() must be called at component scope.
  const { t } = useTranslation();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();
  const [selectedCoach, setSelectedCoach] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{ day: number; start: string; end: string } | null>(null);

  const { data: coaches = [], isLoading: coachesLoading } = useQuery({
    queryKey: ["coaches", organization?.id],
    queryFn: async () => {
      const { data: groups } = await supabase
        .from("training_groups")
        .select("coach_id")
        .eq("organization_id", organization!.id)
        .not("coach_id", "is", null);
      const coachIds = [...new Set((groups ?? []).map((g) => g.coach_id).filter(Boolean) as string[])];
      if (coachIds.length === 0) return [];
      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, avatar_url, level")
        .in("id", coachIds);
      return profiles ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: availability = [] } = useQuery({
    queryKey: ["coach-availability", selectedCoach],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coach_availability")
        .select("*")
        .eq("coach_id", selectedCoach!)
        .eq("is_available", true)
        .order("day_of_week")
        .order("start_time");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!selectedCoach,
  });

  const { data: existingLessons = [] } = useQuery({
    queryKey: ["coach-lessons", selectedCoach],
    queryFn: async () => {
      const { data } = await supabase
        .from("private_lessons")
        .select("*")
        .eq("coach_id", selectedCoach!)
        .eq("status", "confirmed")
        .gte("start_time", new Date().toISOString());
      return data ?? [];
    },
    enabled: !!selectedCoach,
  });

  const bookMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSlot || !selectedCoach || !user || !organization) throw new Error("Missing data");
      const today = new Date();
      const todayDow = today.getDay();
      let daysUntil = selectedSlot.day - todayDow;
      if (daysUntil <= 0) daysUntil += 7;
      const lessonDate = addDays(today, daysUntil);
      const dateStr = format(lessonDate, "yyyy-MM-dd");
      const startTime = new Date(`${dateStr}T${selectedSlot.start}`);
      const endTime = new Date(`${dateStr}T${selectedSlot.end}`);
      const price = 50000; // 500 kr

      // Create private lesson
      const { data: lesson, error } = await supabase.from("private_lessons").insert({
        coach_id: selectedCoach,
        student_id: user.id,
        organization_id: organization.id,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        price,
      }).select("id").single();
      if (error) throw error;

      // Create booking payment (pending) — Sprint D dual-write
      try {
        await supabase.from("booking_payments").insert({
          organization_id: organization.id,
          booking_id: lesson.id, // Using lesson ID as reference
          user_id: user.id,
          amount: price,                              // legacy kronor
          amount_minor: Math.round(price * 100),      // öre
          currency: "SEK",
          status: "pending",
          payment_type: "full",
          payment_method: "pending",
        });
      } catch {
        // Non-critical
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coach-lessons"] });
      toast.success(t("activities:training.lessonBooked"));
      setSelectedSlot(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (coachesLoading) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <GraduationCap className="h-5 w-5" /> Boka tränare
      </h2>

      <div className="grid gap-3 sm:grid-cols-2">
        {coaches.map((coach) => (
          <Card
            key={coach.id}
            className={`cursor-pointer transition-all ${selectedCoach === coach.id ? "ring-2 ring-primary" : "hover:shadow-md"}`}
            onClick={() => { setSelectedCoach(coach.id); setSelectedSlot(null); }}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {(coach.full_name || "?").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{coach.full_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <LevelBadge level={coach.level || 1} size="sm" />
                  <span className="text-xs text-muted-foreground">Tränare</span>
                </div>
              </div>
              {selectedCoach === coach.id && (
                <CheckCircle2 className="h-5 w-5 text-primary ml-auto" />
              )}
            </CardContent>
          </Card>
        ))}
        {coaches.length === 0 && (
          <p className="text-sm text-muted-foreground col-span-2 text-center py-6">Inga tränare tillgängliga</p>
        )}
      </div>

      {selectedCoach && (
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm">Lediga tider</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {availability.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Inga lediga tider angivna</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {availability.map((slot) => {
                  const isSelected = selectedSlot?.day === slot.day_of_week &&
                    selectedSlot?.start === slot.start_time;
                  return (
                    <Button
                      key={slot.id}
                      variant={isSelected ? "default" : "outline"}
                      className="justify-start gap-2 h-auto py-3"
                      onClick={() => setSelectedSlot({
                        day: slot.day_of_week,
                        start: slot.start_time,
                        end: slot.end_time,
                      })}
                    >
                      <Calendar className="h-4 w-4" />
                      <span>{dayNames[slot.day_of_week]}</span>
                      <Clock className="h-3.5 w-3.5 ml-1" />
                      <span>{slot.start_time?.slice(0, 5)} - {slot.end_time?.slice(0, 5)}</span>
                    </Button>
                  );
                })}
              </div>
            )}

            {selectedSlot && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {dayNames[selectedSlot.day]} {selectedSlot.start.slice(0, 5)}-{selectedSlot.end.slice(0, 5)}
                    </p>
                    <p className="text-xs text-muted-foreground">500 kr / lektion</p>
                  </div>
                  <Button onClick={() => bookMutation.mutate()} disabled={bookMutation.isPending}>
                    {bookMutation.isPending && <Loader2 className="animate-spin h-4 w-4 mr-1" />}
                    Boka lektion
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* My upcoming lessons */}
      {existingLessons.length > 0 && (
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm">Mina kommande lektioner</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-2">
            {existingLessons.map((l: any) => (
              <div key={l.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <p className="text-sm font-medium">
                    {format(new Date(l.start_time), "EEEE d MMMM", { locale: sv })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(l.start_time), "HH:mm")} - {format(new Date(l.end_time), "HH:mm")}
                  </p>
                </div>
                <Badge variant={l.payment_status === "paid" ? "default" : "secondary"}>
                  {l.payment_status === "paid" ? "Betald" : "Inväntar betalning"}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
